import type { CapabilityOverride } from "@/services/capabilitiesApi";

export type TripwirePin = "inherit" | "allow" | "deny";

export type TripwireBadgeKind =
  | "killed"
  | "off"
  | "dormant"
  | "on"
  | "pending"
  | "error";

export function isOverrideLive(row: CapabilityOverride): boolean {
  if (row.expires_at == null) return true;
  const expires = Date.parse(row.expires_at);
  if (Number.isNaN(expires)) return true;
  return expires > Date.now();
}

export function indexOverridesBySource(
  rows: CapabilityOverride[] | undefined,
): {
  global: CapabilityOverride | null;
  bySource: Map<string, CapabilityOverride>;
} {
  const bySource = new Map<string, CapabilityOverride>();
  let global: CapabilityOverride | null = null;
  for (const row of rows ?? []) {
    if (!isOverrideLive(row)) continue;
    if (row.scope_type === "global") {
      global = row;
    } else if (row.scope_type === "source") {
      bySource.set(row.scope_id, row);
    }
  }
  return { global, bySource };
}

export function sourcePin(
  bySource: Map<string, CapabilityOverride>,
  sourceId: string,
): TripwirePin {
  const row = bySource.get(sourceId);
  if (!row) return "inherit";
  return row.state === "allow" ? "allow" : "deny";
}

function denyInfo(
  global: CapabilityOverride | null,
  bySource: Map<string, CapabilityOverride>,
  sourceId: string,
): { denied: boolean; globally: boolean } {
  if (global?.state === "deny") {
    return { denied: true, globally: true };
  }
  if (bySource.get(sourceId)?.state === "deny") {
    return { denied: true, globally: false };
  }
  return { denied: false, globally: false };
}

export function tripwireBadgeKind(args: {
  absoluteGlobal: CapabilityOverride | null;
  absoluteBySource: Map<string, CapabilityOverride>;
  scheduledGlobal: CapabilityOverride | null;
  scheduledBySource: Map<string, CapabilityOverride>;
  sourceId: string;
  cronEnabled: boolean | undefined;
  cronPending: boolean;
  overridesPending?: boolean;
  overridesError?: boolean;
}): { kind: TripwireBadgeKind; title: string } {
  if (args.overridesError) {
    return {
      kind: "error",
      title: "Could not load tripwire capability overrides.",
    };
  }

  if (args.overridesPending) {
    return {
      kind: "pending",
      title: "Loading tripwire capability overrides…",
    };
  }

  const killed = denyInfo(
    args.absoluteGlobal,
    args.absoluteBySource,
    args.sourceId,
  );
  if (killed.denied) {
    return {
      kind: "killed",
      title: killed.globally
        ? "Tripwire is killed globally."
        : "Tripwire is killed for this source.",
    };
  }

  const scheduled = denyInfo(
    args.scheduledGlobal,
    args.scheduledBySource,
    args.sourceId,
  );
  if (scheduled.denied) {
    return {
      kind: "off",
      title: scheduled.globally
        ? "Scheduled tripwire sweeps are denied globally. Manual runs still work."
        : "Scheduled tripwire sweeps are denied for this source. Manual runs still work.",
    };
  }

  if (args.cronPending && args.cronEnabled === undefined) {
    return {
      kind: "pending",
      title: "Checking whether cron scheduling is enabled in this environment…",
    };
  }

  if (args.cronEnabled === false) {
    return {
      kind: "dormant",
      title: "",
    };
  }

  return {
    kind: "on",
    title: "Cron scheduling is on. Tripwire checks for this source can run.",
  };
}
